import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, Globe, ChevronsDown } from "lucide-react";
import { Link, useLocation } from "react-router-dom";
import { useLanguage } from "@/context/LanguageContext";
import { navLinks } from "@/lib/nav";

export default function Header() {
  const { t, toggle, lang } = useLanguage();
  const { pathname } = useLocation();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const links = navLinks(t);
  const isTafaker = pathname === "/tafakar";
  const isActive = (l) => (l.id === "tafaker" ? isTafaker : l.id === "contact" ? pathname === "/contact" : l.id === "home" && pathname === "/");

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 inset-x-0 z-50 transition-[background-color,box-shadow] duration-300 backdrop-blur-xl bg-white/80 border-b border-slate-900/5 ${scrolled ? "shadow-[0_8px_30px_rgba(15,23,42,0.06)]" : ""}`}
    >
      <div className="max-w-7xl mx-auto px-5 sm:px-8 h-16 sm:h-20 flex items-center justify-between gap-4">
        <Link to="/" data-testid="brand-logo-link" className="flex items-center gap-2.5 shrink-0">
          <motion.span
            animate={{ y: [0, 3, 0] }}
            transition={{ duration: 1.6, repeat: Infinity, ease: "easeInOut" }}
            className="w-9 h-9 rounded-xl bg-slate-900 flex items-center justify-center"
            data-testid="brand-scroll-icon"
          >
            <ChevronsDown className="text-lime-400" size={20} />
          </motion.span>
          <span className="leading-none">
            <span className="block font-display font-bold text-sm sm:text-base text-slate-900">{t.nav.brandSub}</span>
          </span>
        </Link>

        <nav className="hidden md:flex items-center gap-1" aria-label="Main">
          {links.map((l) => (
            <Link
              key={l.id}
              to={l.to}
              data-testid={`nav-${l.id}-link`}
              className={`px-4 py-2 rounded-full text-sm font-medium hover:text-slate-900 hover:bg-lime-50 transition-colors ${isActive(l) ? "text-slate-900 bg-lime-100" : "text-slate-600"}`}
            >
              {l.label}
            </Link>
          ))}
        </nav>

        <div className="flex items-center gap-2 sm:gap-3">
          <button
            data-testid="language-toggle-button"
            onClick={toggle}
            className="flex items-center gap-1.5 px-3 py-2 rounded-full text-xs font-bold text-slate-700 border border-slate-200 hover:border-lime-400 hover:bg-lime-50 transition-colors"
            aria-label="Switch language"
          >
            <Globe size={14} />
            {lang === "ar" ? "EN" : "عربي"}
          </button>
          <button
            data-testid="mobile-menu-button"
            onClick={() => setOpen(!open)}
            className="md:hidden w-10 h-10 flex items-center justify-center rounded-full border border-slate-200 text-slate-700"
            aria-label="Menu"
          >
            {open ? <X size={18} /> : <Menu size={18} />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            data-testid="mobile-menu"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.25 }}
            className="md:hidden overflow-hidden bg-white/95 backdrop-blur-xl border-t border-slate-100"
          >
            <div className="px-5 py-4 flex flex-col gap-1">
              {links.map((l) => (
                <Link
                  key={l.id}
                  to={l.to}
                  data-testid={`mobile-nav-${l.id}-link`}
                  onClick={() => setOpen(false)}
                  className="px-4 py-3 rounded-xl text-base font-medium text-slate-700 hover:bg-lime-50 hover:text-slate-900 transition-colors"
                >
                  {l.label}
                </Link>
              ))}
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
}
